import { useEffect, useRef, useState } from "react";
import gsap from "gsap";

function CountUpNumber({ target, suffix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const blurRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || startedRef.current) return;
        startedRef.current = true;

        let current = 0;

        const interval = setInterval(() => {
          let step = 1;

          if (target > 1000) {
            step = Math.ceil(target / 100);
          } else if (target > 200) {
            step = 2;
          }

          current += step;

          if (current >= target) {
            current = target;
          }

          setCount(current);

          if (blurRef.current) {
            blurRef.current.style.filter = "blur(6px)";
            blurRef.current.style.opacity = "0.65";
          }

          if (current >= target) {
            clearInterval(interval);

            if (blurRef.current) {
              blurRef.current.style.transition = "all 0.45s ease";
              blurRef.current.style.filter = "blur(0px)";
              blurRef.current.style.opacity = "1";
            }
          }
        }, 16);
      },
      { threshold: 0.4 }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref}>
      <span ref={blurRef}>
        {count.toLocaleString("fr-FR")}
        {suffix}
      </span>
    </span>
  );
}

export default function ImpactStatsSection() {
  const containerRef = useRef(null);

  const stats = [
    { value: 101, label: "Chats adoptés", year: "2025" },
    { value: 40, label: "Chats stérilisés", year: "2025" },
    { value: 25936, label: "Frais vétérinaires", suffix: "€", year: "2025" },
    { value: 59, label: "Adhérents engagés", year: "2025" },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".impact-stat-item",
        { opacity: 0, y: 45 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.14,
          ease: "power3.out",
        }
      );

      gsap.fromTo(
        ".impact-heading",
        { opacity: 0, y: 28 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: "power3.out",
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={containerRef} className="relative overflow-hidden py-24">
      {/* Fond premium */}
      <div className="absolute inset-0 -z-30 bg-[linear-gradient(180deg,#fffdfa_0%,#fbf7f2_45%,#f7f1ea_100%)]" />

      {/* Halos */}
      <div className="absolute inset-0 -z-20 pointer-events-none">
        <div className="absolute left-[4%] top-[8%] h-[360px] w-[360px] rounded-full bg-red-200/30 blur-[120px]" />
        <div className="absolute right-[8%] top-[18%] h-[420px] w-[420px] rounded-full bg-orange-100/35 blur-[130px]" />
        <div className="absolute bottom-[-80px] left-[25%] h-[360px] w-[360px] rounded-full bg-red-100/30 blur-[130px]" />
        <div className="absolute bottom-[10%] right-[20%] h-[220px] w-[220px] rounded-full bg-black/[0.03] blur-[90px]" />
      </div>

      {/* Texture */}
      <div className="absolute inset-0 -z-10 pointer-events-none opacity-[0.04] [background-image:linear-gradient(to_right,black_1px,transparent_1px),linear-gradient(to_bottom,black_1px,transparent_1px)] [background-size:72px_72px]" />

      {/* Fine glow horizontal */}
      <div className="absolute left-1/2 top-[24%] -z-10 h-px w-[70%] -translate-x-1/2 bg-gradient-to-r from-transparent via-red-300/50 to-transparent" />

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="impact-heading mb-14 text-center">
          <p className="text-xs uppercase tracking-[0.35em] text-red-600">
            Notre impact
          </p>

          <h2 className="mt-4 text-4xl font-semibold leading-[1.02] text-[#111111] md:text-6xl">
            Des chiffres qui témoignent
            <span className="block text-red-600">d’un engagement concret.</span>
          </h2>

          <p className="mx-auto mt-6 max-w-2xl text-base leading-8 text-black/60">
            Derrière chaque nombre, il y a des sauvetages, des soins, du temps
            donné, et des vies accompagnées vers une issue plus juste.
          </p>
        </div>

        <div className="relative overflow-hidden rounded-[2.8rem] border border-black/6 bg-white/45 px-6 py-8 shadow-[0_30px_100px_rgba(0,0,0,0.05)] backdrop-blur-2xl md:px-8 md:py-10 xl:px-10 xl:py-12">
          <div className="absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent" />
          <div className="absolute inset-x-10 bottom-0 h-px bg-gradient-to-r from-transparent via-red-200/60 to-transparent" />

          <div className="grid gap-y-10 md:grid-cols-2 md:gap-x-8 xl:grid-cols-4 xl:gap-x-10">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className={`impact-stat-item relative text-center xl:text-left ${
                  index !== stats.length - 1
                    ? "xl:after:absolute xl:after:right-[-20px] xl:after:top-1/2 xl:after:h-24 xl:after:w-px xl:after:-translate-y-1/2 xl:after:bg-gradient-to-b xl:after:from-transparent xl:after:via-black/10 xl:after:to-transparent"
                    : ""
                }`}
              >
                <div className="inline-flex flex-col items-center xl:items-start">
                  <p className="text-5xl font-semibold tracking-tight text-red-700 md:text-6xl xl:text-[68px]">
                    <CountUpNumber target={stat.value} suffix={stat.suffix || ""} />
                  </p>

                  <div className="mt-4 h-px w-20 bg-gradient-to-r from-transparent via-red-300 to-transparent xl:from-red-300 xl:via-red-200 xl:to-transparent" />

                  <p className="mt-5 text-lg font-semibold text-[#4f403d]">
                    {stat.label}
                  </p>

                  {stat.year && (
                    <span className="mt-3 inline-flex rounded-full border border-red-200/70 bg-red-50/80 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.28em] text-red-600 backdrop-blur-md">
                      En {stat.year}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}